import React, { useEffect, useState } from 'react';
import { Filter, UserPlus, Edit2 } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import { Customer } from '../services/api';
import { EditCustomerModal } from '../components/EditCustomerModal';
import { CreateCustomerModal } from '../components/CreateCustomerModal';

export const CustomersPage: React.FC = () => {
  const { customers, loading, refresh } = useDashboard();
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [displayCustomers, setDisplayCustomers] = useState<Customer[]>([]);

  useEffect(() => {
    setDisplayCustomers(customers);
  }, [customers]);

  const truncateMessage = (msg?: string, maxLength = 40) => {
    if (!msg) return '—';
    return msg.length > maxLength ? msg.substring(0, maxLength) + '...' : msg;
  };

  const handleSaveCustomer = (updatedCustomer: Customer) => {
    setDisplayCustomers((prev) => {
      const hasCustomer = prev.some((customer) => customer._id === updatedCustomer._id);
      if (!hasCustomer) {
        return [updatedCustomer, ...prev];
      }
      return prev.map((customer) =>
        customer._id === updatedCustomer._id ? updatedCustomer : customer
      );
    });
    refresh();
  };

  const handleCreateCustomer = (createdCustomer: Customer) => {
    setDisplayCustomers((prev) => {
      const withoutCreated = prev.filter((customer) => customer._id !== createdCustomer._id);
      return [createdCustomer, ...withoutCreated];
    });
    refresh();
  };

  return (
    <div className="page-section">
      <header className="page-heading">
        <div>
          <p className="eyebrow">Customer Directory</p>
          <h2>All WhatsApp contacts</h2>
        </div>
        <div className="heading-actions">
          <button className="ghost-button">
            <Filter size={16} />
            Filters
          </button>
          <button className="primary-button" onClick={() => setIsCreateModalOpen(true)}>
            <UserPlus size={16} />
            Add contact
          </button>
        </div>
      </header>

      <div className="panel">
        <div className="table table-customers" style={{ gridTemplateColumns: '1.5fr 1fr 1.2fr 1fr 1.5fr 0.5fr' }}>
          <div className="table-head">
            <span>Lead Name</span>
            <span>WhatsApp Name</span>
            <span>WhatsApp ID</span>
            <span>Phone</span>
            <span>Last Message</span>
            <span>Actions</span>
          </div>
          <div className="table-body">
            {loading ? (
              <div className="empty-state">
                <div className="spinner small"></div>
                <p>Loading contacts...</p>
              </div>
            ) : displayCustomers.length === 0 ? (
              <div className="empty-state">
                <p>No customers yet.</p>
              </div>
            ) : (
              displayCustomers.map((customer) => (
                <div key={customer._id} className="table-row">
                  <span>
                    <strong>{customer.name || <em style={{ opacity: 0.5 }}>Not set</em>}</strong>
                  </span>
                  <span style={{ opacity: customer.whatsappName ? 1 : 0.5 }}>
                    {customer.whatsappName || '—'}
                  </span>
                  <span style={{ fontSize: '0.85em', fontFamily: 'monospace', opacity: 0.8 }}>
                    {customer.whatsappId?.split('@')[0]?.substring(0, 15) || '—'}
                  </span>
                  <span>
                    {customer.phoneNumber ? (
                      <span className="pill success">{customer.phoneNumber}</span>
                    ) : (
                      <span className="pill warning">Missing</span>
                    )}
                  </span>
                  <span style={{ fontSize: '0.85em', opacity: 0.8 }}>
                    {truncateMessage(customer.metadata?.lastMessagePreview)}
                  </span>
                  <span>
                    <button
                      className="ghost-button"
                      style={{ padding: '4px 8px' }}
                      onClick={() => setEditingCustomer(customer)}
                    >
                      <Edit2 size={14} />
                    </button>
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <CreateCustomerModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreateCustomer}
      />

      {/* Edit Customer Modal */}
      <EditCustomerModal
        customer={editingCustomer}
        isOpen={!!editingCustomer}
        onClose={() => setEditingCustomer(null)}
        onSave={handleSaveCustomer}
      />
    </div>
  );
};
